/**
 * BrewNectar Header — scroll state, mobile menu, smooth anchor navigation
 */
(function () {
  'use strict';

  function closeAllAccountMenus() {
    document.querySelectorAll('[data-bn-account]').forEach(function (account) {
      const trigger = account.querySelector('[data-bn-account-trigger]');
      const menu = account.querySelector('[data-bn-account-menu]');
      if (!trigger || !menu) return;
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      account.classList.remove('is-open');
    });
  }

  function initAccountMenu(account) {
    if (!account || account.dataset.bnAccountInit === 'true') return;
    account.dataset.bnAccountInit = 'true';

    const trigger = account.querySelector('[data-bn-account-trigger]');
    const menu = account.querySelector('[data-bn-account-menu]');
    if (!trigger || !menu) return;

    function openMenu() {
      closeAllAccountMenus();
      trigger.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
      account.classList.add('is-open');
    }

    function closeMenu() {
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      account.classList.remove('is-open');
    }

    // Click/tap toggle (CSS :hover handles desktop mouse without JS)
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (account.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Keep aria state in sync when CSS hover opens the menu
    account.addEventListener('mouseenter', function () {
      if (window.innerWidth < 750) return;
      trigger.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
    });

    account.addEventListener('mouseleave', function () {
      if (window.innerWidth < 750) return;
      if (account.classList.contains('is-open')) return;
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    });

    menu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });
  }

  function bindGlobalListeners() {
    if (bindGlobalListeners.bound) return;
    bindGlobalListeners.bound = true;

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllAccountMenus();
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-bn-account]')) {
        closeAllAccountMenus();
      }
    });
  }

  function initHeader(section) {
    if (!section || section.dataset.bnHeaderInit === 'true') return;
    section.dataset.bnHeaderInit = 'true';

    const header = section.querySelector('[data-bn-header]');
    const toggle = section.querySelector('[data-bn-mobile-toggle]');
    const panel = section.querySelector('[data-bn-mobile-panel]');
    const anchorLinks = section.querySelectorAll('[data-bn-anchor]');
    const scrollThreshold = parseInt(section.dataset.scrollThreshold || '20', 10);
    const homeUrl = section.dataset.homeUrl || '/';

    function isHomePage() {
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      const home = homeUrl.replace(/\/$/, '') || '/';
      return path === home || path === '';
    }

    function setScrolled() {
      if (!header) return;
      const menuOpen = header.classList.contains('is-menu-open');
      header.classList.toggle('is-scrolled', menuOpen || window.scrollY > scrollThreshold);
    }

    function closeMobileMenu() {
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bn-mobile-menu-open');
      if (header) header.classList.remove('is-menu-open');
      setScrolled();
    }

    function openMobileMenu() {
      if (!toggle || !panel) return;
      closeAllAccountMenus();
      toggle.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bn-mobile-menu-open');
      if (header) header.classList.add('is-menu-open');
      setScrolled();
    }

    function toggleMobileMenu() {
      if (!toggle) return;
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    }

    function scrollToAnchor(anchorId) {
      const el = document.getElementById(anchorId);
      if (el) {
        closeMobileMenu();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + anchorId);
      }
    }

    function handleAnchorClick(e) {
      const anchorId = e.currentTarget.getAttribute('data-bn-anchor');
      if (!anchorId) return;

      if (isHomePage()) {
        e.preventDefault();
        scrollToAnchor(anchorId);
      }
      /* Off homepage: let default href (/#anchor) navigate */
    }

    setScrolled();
    window.addEventListener('scroll', setScrolled, { passive: true });

    if (toggle) {
      toggle.addEventListener('click', toggleMobileMenu);
    }

    anchorLinks.forEach(function (link) {
      link.addEventListener('click', handleAnchorClick);
    });

    bindGlobalListeners();
    section.querySelectorAll('[data-bn-account]').forEach(initAccountMenu);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMobileMenu();
    });

    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth >= 750) {
          closeMobileMenu();
        } else {
          closeAllAccountMenus();
        }
      },
      { passive: true }
    );

    /* Smooth scroll to hash on page load */
    if (isHomePage() && window.location.hash) {
      const id = window.location.hash.slice(1);
      requestAnimationFrame(function () {
        scrollToAnchor(id);
      });
    }

    /* Logo scroll to top on homepage */
    const logo = section.querySelector('[data-bn-logo-home]');
    if (logo) {
      logo.addEventListener('click', function (e) {
        if (isHomePage()) {
          e.preventDefault();
          closeMobileMenu();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-bn-header-section]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('[data-bn-header-section]') || e.target;
    if (section.matches && section.matches('[data-bn-header-section]')) {
      section.dataset.bnHeaderInit = '';
      section.querySelectorAll('[data-bn-account]').forEach(function (account) {
        account.dataset.bnAccountInit = '';
      });
      initHeader(section);
    }
  });
})();
